import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import AdvertiserDetailsSection from "./AdvertiserDetailsSection";
import Breadcrumps from "../../components/modules/Breadcrumps";
import CampaignsDashboard from "./CampaignsDashboard";
import CampaignsDialog from "./CampaignsDialog";
import CampaignsDialogControl from "./CampaignsDialogControl";
import Loader from "../../components/elements/Loader";
import PageLayout from "../../layouts/PageLayout";
import api from "../../api";
import classes from "./AdvertiserDetailPage.module.scss";
import { AdvertiserType } from "../../types/advertiserTypes";
import { CampaignType, ReportTimeFrames } from "../../types/campaignTypes";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { setCampaignToCreateSelectedAdvertiser } from "../../redux/reducers/campaignToCreate";
import { truncateText } from "../../helpers/truncateText";

const fetchAdvertiserById = async (bgAdvertiserId: string) => {
  try {
    const { payload } = await api.getAdvertiserById(bgAdvertiserId);
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const fetchAdvertiserCampaigns = async (bgAdvertiserId: string, timeFrame: string) => {
  try {
    const { payload } = await api.getAllCampaignsByAdvertiser(bgAdvertiserId, timeFrame);
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const AdvertiserDetailPage = () => {
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const [isLoadingAdvertiserDetailData, setIsLoadingAdvertiserDetailData] = useState(false);
  const [selectedTimeFrame, setSelectedTimeFrame] = React.useState<ReportTimeFrames>(ReportTimeFrames.ALL_TIME);
  const [advertiserDetailData, setAdvertiserDetailData] = useState<AdvertiserType | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignType[]>([]);
  const [unarchivedCampaigns, setUnarchivedCampaigns] = useState<CampaignType[]>([]);

  const [isCampaignsDialogOpen, setIsCampaignsDialogOpen] = useState(false);
  const [wereCampaignsEdited, setWereCampaignsEdited] = useState(false);

  const params: { id: string } = useParams();
  const mounted = useRef(false);
  const dispatch = useDispatch();

  const fetchAndSetAdvertiserDetailData = useCallback(async () => {
    try {
      setIsLoadingAdvertiserDetailData(true);
      const fetchedAdvertiserDetailData = await fetchAdvertiserById(params.id);
      setAdvertiserDetailData(fetchedAdvertiserDetailData);
      setIsLoadingAdvertiserDetailData(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAndSetAdvertiserDetailData error: ", error);
      setIsLoadingAdvertiserDetailData(false);
    }
  }, [params.id]);

  const fetchAndSetCampaings = useCallback(async () => {
    try {
      const fetchedAdvertiserCampaigns = (await fetchAdvertiserCampaigns(
        params.id,
        selectedTimeFrame,
      )) as CampaignType[];
      setCampaigns(fetchedAdvertiserCampaigns);
      setUnarchivedCampaigns(fetchedAdvertiserCampaigns.filter((campaign) => !campaign.archived));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAndSetCampaigns error: ", error);
    }
  }, [params.id, selectedTimeFrame]);

  useEffect(() => {
    fetchAndSetAdvertiserDetailData();
  }, [fetchAndSetAdvertiserDetailData, params.id]);

  useEffect(() => {
    if (!mounted.current) {
      fetchAndSetCampaings();
      mounted.current = true;
    } else if (wereCampaignsEdited) {
      fetchAndSetCampaings();
      setWereCampaignsEdited(false);
    }
  }, [fetchAndSetCampaings, wereCampaignsEdited]);

  useEffect(() => {
    fetchAndSetCampaings();
  }, [fetchAndSetCampaings, selectedTimeFrame]);

  const handleOpenCampaignsDialog = useCallback(() => setIsCampaignsDialogOpen(true), []);
  const handleCloseCampaignsDialog = () => setIsCampaignsDialogOpen(false);

  const handleTimeFrameChange = useCallback(
    (
      event: React.ChangeEvent<{
        value: unknown;
      }>,
    ) => {
      setSelectedTimeFrame(event.target.value as ReportTimeFrames);
    },
    [],
  );

  const breadcrumps = [
    { label: "Home", link: "/dashboard", active: false },
    { label: "Advertisers", link: "/advertisers", active: false },
    { label: truncateText(advertiserDetailData?.name ?? "...", 36), link: `/advertisers/${params.id}`, active: true },
  ];

  useEffect(() => {
    if (advertiserDetailData) dispatch(setCampaignToCreateSelectedAdvertiser(advertiserDetailData));
  }, [advertiserDetailData, dispatch]);

  return (
    <PageLayout heading="Account information" heightAuto>
      <Breadcrumps breadcrumps={breadcrumps} className={classes.breadcrumps} />

      {!advertiserDetailData || isLoadingAdvertiserDetailData ? (
        <Loader />
      ) : (
        <>
          <AdvertiserDetailsSection
            fetchAndSetAdvertiserDetailData={fetchAndSetAdvertiserDetailData}
            advertisers={advertisers}
            advertiser={advertiserDetailData}
          />

          <>
            {!unarchivedCampaigns.length && campaigns.length ? (
              <CampaignsDialogControl handleOpenCampaignsDialog={handleOpenCampaignsDialog} />
            ) : unarchivedCampaigns.length ? (
              <CampaignsDashboard
                campaigns={unarchivedCampaigns}
                selectedTimeFrame={selectedTimeFrame}
                handleTimeFrameChange={handleTimeFrameChange}
                fetchAndSetCampaings={fetchAndSetCampaings}
                handleOpenCampaignsDialog={handleOpenCampaignsDialog}
              />
            ) : null}

            {!campaigns.length ? null : (
              <CampaignsDialog
                isOpen={isCampaignsDialogOpen}
                handleClose={handleCloseCampaignsDialog}
                campaigns={campaigns}
                advertiserName={advertiserDetailData.name}
                setWereCampaignsEdited={setWereCampaignsEdited}
              />
            )}
          </>
        </>
      )}
    </PageLayout>
  );
};

export default AdvertiserDetailPage;
